import { Grid } from '@mui/material'
import { GridRenderCellParams } from '@mui/x-data-grid/models/params/gridCellParams'

import { IGridColumn, FplDataGrid } from 'components/common'
import {
  BookingNotAcceptedByDriver,
  MissingPocPod,
  CollectionDeliveryDelay,
  DriverOnboard,
  EtaProvider,
} from 'constants/EntityConstants/SchedulerAttempts'
import { GET_BOOKING_SCHEDULER_CONTROLS } from 'graphql/queries/BookingSchedulerControlQueries'

import BookingSchedulerControlsColumnsMeaning from './BookingSchedulerControlsColumnsMeaning'
import BookingSchedulerControlsStatusLegend from './BookingSchedulerControlsStatusLegend'
import BookingSchedulerControlSwitch from './BookingSchedulerControlSwitch'

interface OverviewTabProps {
  bookingId: string | null
}

const SchedulerNameFormatter = (name: string) => {
  switch (name) {
    case BookingNotAcceptedByDriver:
      return 'Booking Not Accepted'
    case MissingPocPod:
      return 'Missing POC/POD'
    case CollectionDeliveryDelay:
      return 'Collection/Delivery Delay'
    case DriverOnboard:
      return 'Driver Onboarding'
    case EtaProvider:
      return 'ETA Provider'
    default:
      return name
  }
}

const BookingSchedulerControls = ({ bookingId }: OverviewTabProps) => {
  const columns: IGridColumn[] = [
    {
      field: 'action',
      headerName: 'ON/OFF',
      hideable: false,
      width: 100,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: ({ row }: GridRenderCellParams) => (
        <BookingSchedulerControlSwitch
          schedulerType={row.schedulerType}
          state={row.isEnabledForBooking}
          bookingId={bookingId}
        />
      ),
    },
    {
      field: 'isAvailableGlobally',
      headerName: 'Global',
      hideable: false,
      disableColumnMenu: true,
      minWidth: 80,
      flex: 1,
      sortable: false,
      valueFormatter: ({ value }) => (value ? 'Enabled' : 'Disabled'),
    },
    {
      field: 'schedulerType',
      headerName: 'Scheduler',
      hideable: false,
      disableColumnMenu: true,
      minWidth: 180,
      flex: 1,
      sortable: false,
      valueFormatter: ({ value }) => SchedulerNameFormatter(value),
    },
    {
      field: 'context',
      headerName: 'Context (Driver ID)',
      hideable: false,
      disableColumnMenu: true,
      minWidth: 180,
      flex: 1,
      sortable: false,
      valueFormatter: ({ value }) => value ?? 'N/A'
    },
    {
      field: 'isInitialized',
      headerName: 'Initialized',
      hideable: false,
      disableColumnMenu: true,
      minWidth: 100,
      flex: 1,
      type: 'string',
      sortable: false,
      valueFormatter: ({ value }) => (value ? 'Yes' : 'No'),
    },
    {
      field: 'lastAttemptAt',
      headerName: 'Last Attempt',
      hideable: false,
      disableColumnMenu: true,
      minWidth: 180,
      flex: 1,
      sortable: false,
      valueFormatter: ({ value }) =>
        value === null ? 'N/A' : new Date(value).toLocaleString() ?? 'N/A',
    },
    {
      field: 'lastOutcome',
      headerName: 'Last Outcome',
      hideable: false,
      disableColumnMenu: true,
      minWidth: 120,
      flex: 1,
      sortable: false,
      valueFormatter: ({ value }) => value ?? 'N/A',
    },
    {
      field: 'currentStatus',
      headerName: 'Current Status',
      hideable: false,
      disableColumnMenu: true,
      minWidth: 120,
      flex: 1,
      sortable: false,
      valueFormatter: ({ value }) => value ?? 'N/A',
    },
    {
      field: 'retryCount',
      headerName: 'Retries',
      hideable: false,
      disableColumnMenu: true,
      minWidth: 80,
      flex: 1,
      sortable: false,
    },
    {
      field: 'nextAttemptAt',
      headerName: 'Next Attempt',
      hideable: false,
      disableColumnMenu: true,
      minWidth: 180,
      flex: 1,
      sortable: false,
      valueFormatter: ({ value }) =>
        value === null ? 'N/A' : new Date(value).toLocaleString() ?? 'N/A',
    },
  ]

  return (
    <FplDataGrid
      query={GET_BOOKING_SCHEDULER_CONTROLS}
      queryVariables={{ id: Number(bookingId) }}
      fetchPolicy='network-only'
      entityName='bookingSchedulerControls'
      columns={columns}
      defaultOrder={{ field: 'schedulerType', sort: 'asc' }}
      toolbar={{
        caption: 'Booking Scheduler Controls',
        rightSide: (
          <Grid item>
            <BookingSchedulerControlsColumnsMeaning />
            <BookingSchedulerControlsStatusLegend />
          </Grid>
        ),
      }}
      disableSelectionOnClick
    />
  )
}

export default BookingSchedulerControls
